import React, { useEffect, useState } from "react";
import { Qa } from "../qa";
import Space from "components/common/space";
import T from "@marginly/ui/components/typography";
import { useMemberInfo } from "components/registration/hooks/use-member-info";
import { HeroLayout } from "../styled/hero-layout";
import { useAccount } from "wagmi";
import { useReferralsList } from "components/leaderboard/hooks/use-referrals-list";
import { RefferalRankingModal } from "components/referral-ranking-modal";
import { LeaderboardSection } from "components/leaderboard-section";
import { IndividualRatingModal } from "components/individual-rating-modal";
import { useMemberPlacement } from "components/leaderboard/hooks/use-member-placement";
import { EmailButton } from "components/leaderboard/styled";
import {
  getCamelotPrizeNumber,
  getCamelotRefPrizeNumber,
} from "components/leaderboard/utils/get-prize-number";
import {
  BADGES_CAMELOT,
  REFER_BADGES_CAMELOT,
} from "components/leaderboard/hooks/use-rating";
import { RankInfo } from "../rank-info";
import { useLeaderboardList } from "components/leaderboard/hooks/use-leaderboard-list";
import ConnectWalletModal from "components/common/navigation/connect-wallet";
import { formatNum } from "util/format";

const getRefRankImg = (placeNumber: number | null | undefined) => {
  return `/contest/images/referral/thumbnail-${getCamelotRefPrizeNumber(
    placeNumber,
  )}.png`;
};

const getRankImg = (placeNumber: number | null | undefined) => {
  return `/contest/images/prizes/${getCamelotPrizeNumber(placeNumber)}.png`;
};

const TOP_RANK = 50;

interface Props {
  contestId: number;
}

export function CamelotEnded({ contestId }: Props) {
  const { memberInfo } = useMemberInfo(contestId);
  const [isIndividualVisible, setIsIndividualVisible] = useState(false);
  const [isWalletModalVisible, setIsWalletModalVisible] = React.useState(false);

  const hideWalletModal = () => {
    document.body.style.overflow = "visible";
    setIsWalletModalVisible(false);
  };
  const [isReferralRankingVisible, setIsReferralRankingVisible] =
    useState(false);
  const { address } = useAccount();
  const [userAddress, setUserAddress] = useState<string | undefined>(undefined);
  useEffect(() => {
    setUserAddress(address);
  }, [address]);

  const referralsList = useReferralsList(contestId);
  const leaderboardList = useLeaderboardList(contestId);
  const refCode = memberInfo?.refCode;
  const memberPlacement = useMemberPlacement(contestId);
  const rank = memberPlacement?.place === -1 ? null : memberPlacement?.place;

  const userReferralIndex = referralsList?.top?.findIndex(
    (item) => item.userAddress === userAddress,
  );

  const userReferralPlace = userReferralIndex === -1 ? null : userReferralIndex;

  const renderInfo = (prizeSum: number) => {
    if (!prizeSum)
      return (
        <>
          <T headerL>You didn&apos;t win anything :(</T>
          <Space height={12} heightMobile={4} />
          <T secondary body>
            Your rank is #{formatNum(rank || undefined)}. Maybe you&apos;ll have
            better luck next time?
          </T>
        </>
      );

    return (
      <>
        <T headerL>You won ${formatNum(prizeSum)}</T>
        <Space height={12} heightMobile={4} />
        <T secondary body>
          Your rank is #{formatNum(rank || undefined)}
          {userReferralPlace
            ? ` and referrer rank
          is #${formatNum(userReferralPlace || undefined)}`
            : ""}
          . Congratulations!
        </T>
      </>
    );
  };

  const renderTop = () => {
    if (!userAddress) {
      return (
        <>
          <T headerL>Camelot competition has ended</T>
          <Space height={12} heightMobile={12} />
          <T body secondary>
            Connect wallet to see your rank in the contest.
          </T>
          <Space height={48} heightMobile={48} />
          <EmailButton
            className="white"
            onClick={() => {
              document.body.style.overflow = "hidden";
              setIsWalletModalVisible(true);
            }}
          >
            <T action>Check rank</T>
          </EmailButton>
        </>
      );
    }
    if (!refCode || !rank) {
      return (
        <>
          <T headerL>You didn’t participate</T>
        </>
      );
    }

    return (
      <RankInfo
        badges={BADGES_CAMELOT}
        getPrizeNumber={getCamelotPrizeNumber}
        getRankImg={getRankImg}
        getRefRankImg={getRefRankImg}
        userReferralPlace={userReferralPlace}
        getReferralPrizeNumber={getCamelotRefPrizeNumber}
        referBadges={REFER_BADGES_CAMELOT}
        topRank={TOP_RANK}
        memberPlacement={memberPlacement}
        rank={rank}
        renderInfo={renderInfo}
      />
    );
  };

  return (
    <div>
      <HeroLayout>{renderTop()}</HeroLayout>
      <Space height={172} heightMobile={72} />
      <LeaderboardSection
        contestDates="Mar 13 to Mar 27"
        renderImgReferPath={(placeNumber: number) =>
          `/contest/images/referral/thumbnail-${placeNumber}.png`
        }
        isContestOver={true}
        list={leaderboardList}
        onTopRatingButtonClick={() => setIsIndividualVisible(true)}
        userAddress={userAddress}
        renderImgPath={getRankImg}
        topRank={TOP_RANK}
      />
      <Space height={172} heightMobile={96} />
      <Qa />
      {isReferralRankingVisible && (
        <RefferalRankingModal
          renderImgPath={getRefRankImg}
          onHide={() => setIsReferralRankingVisible(false)}
          address={userAddress}
          list={referralsList}
        />
      )}
      {isIndividualVisible && (
        <IndividualRatingModal
          topRank={TOP_RANK}
          renderImgPath={getRankImg}
          onHide={() => setIsIndividualVisible(false)}
          userAddress={userAddress}
          list={leaderboardList}
        />
      )}
      {isWalletModalVisible ? (
        <ConnectWalletModal onHide={hideWalletModal} />
      ) : (
        <></>
      )}
    </div>
  );
}
